import { Link } from 'react-router-dom';
import ImageGallery from '../../components/ImageGallery';
import './UncoilersPresses.css';

// Import uncoiler and press images
import uncoiler4000 from '../../assets/products/uncoilers & presses/4000lbuncoiler.jpg';
import uncoiler6000 from '../../assets/products/uncoilers & presses/6000lbuncoiler.jpg';
import airPresses from '../../assets/products/uncoilers & presses/air-presses-enlarge.jpg';
import hydraulicPresses from '../../assets/products/uncoilers & presses/hydraulic-presses-enlarge.jpg';
import mechanicalPresses from '../../assets/products/uncoilers & presses/mechanical-presses-enlarge.jpg';

const UncoilersPresses = () => {
  const equipmentImages = [
    { src: uncoiler4000, alt: '4000lb Capacity Uncoiler', title: '4000lb Heavy-Duty Uncoiler' },
    { src: uncoiler6000, alt: '6000lb Capacity Uncoiler', title: '6000lb Industrial Uncoiler' },
    { src: hydraulicPresses, alt: 'Hydraulic Press Systems', title: 'Hydraulic Press Equipment' },
    { src: mechanicalPresses, alt: 'Mechanical Press Systems', title: 'Mechanical Press Solutions' },
    { src: airPresses, alt: 'Pneumatic Press Systems', title: 'Air-Powered Press Equipment' },
  ];

  const uncoilerSpecs = [
    { model: '4000lb Standard', capacity: '4000 lbs', width: '18" - 24"', motorized: 'Optional', features: 'Manual brake, adjustable mandrel' },
    { model: '6000lb Heavy-Duty', capacity: '6000 lbs', width: '24" - 30"', motorized: 'Standard', features: 'Hydraulic expansion, automatic brake' },
    { model: '10000lb Industrial', capacity: '10000 lbs', width: '30" - 48"', motorized: 'Standard', features: 'Hydraulic expansion, servo control' },
    { model: 'Custom Capacity', capacity: 'Up to 20000 lbs', width: 'Custom', motorized: 'Available', features: 'Engineered to specification' },
  ];

  const pressSpecs = [
    { type: 'Hydraulic', capacity: '10 - 600 tons', features: 'Variable speed, precise control, high force', applications: 'Forming, punching, deep drawing' },
    { type: 'Mechanical', capacity: '20 - 400 tons', features: 'High-speed operation, consistent stroke', applications: 'Blanking, piercing, coining' },
    { type: 'Pneumatic', capacity: '1 - 50 tons', features: 'Quick cycling, clean operation', applications: 'Light assembly, marking, riveting' },
    { type: 'Servo Electric', capacity: '5 - 200 tons', features: 'Precise positioning, energy efficient', applications: 'Precision forming, assembly' },
  ];

  const features = [
    {
      title: 'Uncoiler Systems',
      description: 'Heavy-duty uncoiling equipment for continuous material feeding.',
      capabilities: [
        'Capacities from 1000 to 20000 lbs',
        'Material widths up to 60 inches',
        'Motorized and non-motorized options',
        'Hydraulic expansion mandrels',
        'Servo-controlled positioning',
        'Automatic brake systems'
      ]
    },
    {
      title: 'Hydraulic Presses',
      description: 'Precision hydraulic press systems for forming and fabrication.',
      capabilities: [
        'Force ratings 10 to 600 tons',
        'Variable speed control',
        'Programmable stroke length',
        'Safety light curtains',
        'Custom tooling integration',
        'PLC control systems'
      ]
    },
    {
      title: 'Mechanical Presses',
      description: 'High-speed mechanical presses for production applications.',
      capabilities: [
        'Capacities 20 to 400 tons',
        'Speeds up to 300 SPM',
        'Clutch and brake systems',
        'Adjustable stroke',
        'Die cushions available',
        'Safety monitoring'
      ]
    },
    {
      title: 'Custom Solutions',
      description: 'Engineered systems designed for specific applications.',
      capabilities: [
        'Custom capacity requirements',
        'Specialized tooling integration',
        'Automated material handling',
        'Quality monitoring systems',
        'Process optimization',
        'Turnkey installation'
      ]
    }
  ];

  const applications = [
    {
      industry: 'Automotive',
      uses: ['Body panel forming', 'Bracket fabrication', 'Structural components', 'Trim parts production']
    },
    {
      industry: 'Appliance',
      uses: ['Cabinet forming', 'Component assembly', 'Door panel production', 'Bracket manufacturing']
    },
    {
      industry: 'Construction',
      uses: ['Structural steel forming', 'Roofing components', 'Building hardware', 'Metal framing']
    },
    {
      industry: 'Electronics',
      uses: ['Enclosure fabrication', 'Heat sink forming', 'Chassis production', 'Mounting brackets']
    }
  ];

  return (
    <div className="uncoilers-presses-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/products">Products</Link> / Uncoilers & Presses
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1>Uncoilers & Presses</h1>
          <p className="page-subtitle">
            Heavy-duty uncoiling and pressing equipment for continuous production
          </p>
        </div>

        {/* Overview Section */}
        <section className="overview-section">
          <div className="overview-content">
            <h2>Complete Material Handling & Forming Solutions</h2>
            <p>
              Our uncoilers and presses are essential components of any roll forming operation. 
              From heavy-duty uncoilers that handle coils up to 20,000 lbs to precision presses 
              with capacities up to 600 tons, we provide the equipment you need for efficient production.
            </p>
            <p>
              Whether you need standard models or custom-engineered solutions, our equipment 
              is built for reliability, safety, and long service life in demanding industrial environments.
            </p>
          </div>
        </section>

        {/* Equipment Features */}
        <section className="features-section">
          <h2>Equipment Categories</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <ul>
                  {feature.capabilities.map((capability, idx) => (
                    <li key={idx}>{capability}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Uncoiler Specifications */}
        <section className="specifications-section">
          <h2>Uncoiler Specifications</h2>
          <div className="specs-table-wrapper">
            <table className="specs-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Capacity</th>
                  <th>Material Width</th>
                  <th>Motorized</th>
                  <th>Key Features</th>
                </tr>
              </thead>
              <tbody>
                {uncoilerSpecs.map((spec, index) => (
                  <tr key={index}>
                    <td>{spec.model}</td>
                    <td>{spec.capacity}</td>
                    <td>{spec.width}</td>
                    <td>{spec.motorized}</td>
                    <td>{spec.features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Press Specifications */}
        <section className="specifications-section">
          <h2>Press System Specifications</h2>
          <div className="specs-table-wrapper">
            <table className="specs-table">
              <thead>
                <tr>
                  <th>Press Type</th>
                  <th>Capacity Range</th>
                  <th>Key Features</th>
                  <th>Primary Applications</th>
                </tr>
              </thead>
              <tbody>
                {pressSpecs.map((spec, index) => (
                  <tr key={index}>
                    <td>{spec.type}</td>
                    <td>{spec.capacity}</td>
                    <td>{spec.features}</td>
                    <td>{spec.applications}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Applications by Industry */}
        <section className="applications-section">
          <h2>Industry Applications</h2>
          <div className="applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="application-card">
                <h3>{app.industry}</h3>
                <ul>
                  {app.uses.map((use, idx) => (
                    <li key={idx}>{use}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Image Gallery */}
        <section className="gallery-section">
          <h2>Equipment Gallery</h2>
          <p>View our range of uncoilers and press systems in operation.</p>
          <ImageGallery images={equipmentImages} columns={3} />
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-content">
            <h2>Need Custom Equipment Solutions?</h2>
            <p>
              Our engineering team can design uncoilers and presses to meet your specific 
              production requirements. Contact us to discuss your application.
            </p>
            <div className="cta-buttons">
              <Link to="/quote" className="btn btn-primary">Request Equipment Quote</Link>
              <Link to="/contact" className="btn btn-secondary">Discuss Requirements</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UncoilersPresses;